import React, { useRef, useEffect, memo } from "react";
import * as d3 from "d3";
import { BarChartDataItemProps } from "./ChartTypes";
import { formatBandwidth } from "@utils/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface DonutChartProps {
  parentid?: string;
  data: BarChartDataItemProps[];
  dataType?: string;
  customLabel?: string;
  handleClick?: (label: string, e: any) => void;
  clickReturn?: string;
  sortFunc?: string;
}

const BarChart: React.FC<DonutChartProps> = memo(
  ({
    parentid,
    data,
    dataType = "number",
    customLabel = "",
    handleClick = () => {},
    clickReturn = "label",
    sortFunc = "DESC",
  }) => {
    const scale = window.innerWidth / 1920;
    const svgRef = useRef<SVGSVGElement>(null);
    const parentRef = useRef<HTMLDivElement>(null);
    const legendRef = useRef<HTMLDivElement>(null);
    const [groupColor, setGroupColor] = React.useState<{
      [key: string]: string;
    }>({});

    useEffect(() => {
      if (!svgRef.current) return;
      d3.select(svgRef.current).selectAll("*").remove();

      const parentElement = parentid
        ? document.getElementById(parentid)
        : parentRef.current;
      if (!parentElement) {
        // Handle the error
        console.error(`No element with ID ${parentid}`);
        return;
      }

      const groups: string[] = [];
      data.forEach((d) => {
        if (d.group && !groups.includes(d.group)) {
          groups.push(d.group);
        }
      });

      const gc: any = {};
      groups.forEach((g, i) => {
        gc[g] = "var(--chart-" + (i + 1) + ")";
      });
      setGroupColor(gc);

      const rect = parentElement.getBoundingClientRect();

      let longestLabel = "";
      data.forEach((d) => {
        if (d.label.length > longestLabel.length) {
          longestLabel = d.label;
        }
      });

      let longestValue = longestLabel.length * (10 * scale);
      if (longestValue > rect.width / 3) {
        longestValue = rect.width / 3;
      }

      const margin = { top: 10, right: 30, bottom: 10, left: longestValue };
      const width = rect.width - margin.left - margin.right;
      const height = rect.height - margin.top - margin.bottom;
      const svg = d3
        .select(svgRef.current)
        .attr("width", rect.width)
        .attr("height", rect.height)
        .append("g")
        .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

      // data.sort((a, b) => b.value - a.value);

      data.sort((a, b) => {
        return sortFunc === "ASC"
          ? a.value - b.value
          : sortFunc == "DESC"
            ? b.value - a.value
            : 0;
      });
      data = data.filter((d) => d.value > 0);
      if (data.length > 10) {
        data = data.slice(0, 10);
      }

      const labelCounts: Record<string, number> = {};

      const updatedData = data.map((item) => {
        const count = (labelCounts[item.label] || 0) + 1;
        labelCounts[item.label] = count;

        return {
          ...item,
          alt_labels: count > 1 ? `${item.label} (${count})` : item.label,
        };
      });
      data = updatedData;

      // Combine duplicate labels by summing their values
      // const combinedData = data.reduce(
      //   (acc: any, current: BarChartDataItemProps) => {
      //     const existingItem = acc.find(
      //       (item: BarChartDataItemProps) => item.label === current.label,
      //     );
      //     if (existingItem) {
      //       existingItem.value += current.value;
      //     } else {
      //       acc.push({ ...current });
      //     }
      //     return acc;
      //   },
      //   [],
      // );

      // Replace data with combinedData
      // data = combinedData;

      const max = data.reduce((acc, current) => {
        return Math.max(acc, current.value);
      }, -Infinity);

      // Add X axis
      const x = d3
        .scaleLinear()
        .domain([0, max])
        .range([0, width - 24]);

      // Y axis
      const y = d3
        .scaleBand()
        .range([0, height])
        .domain(
          data.map(function (d) {
            return d.alt_labels;
          }),
        )
        .padding(0.7);

      svg
        .append("g")
        .call(
          d3.axisLeft(y).tickFormat(function (d) {
            return d;
          }),
        )
        .selectAll("text")
        .style("cursor", "pointer")
        .style("fill", "var(--foreground)")
        .style("font-size", "0.7rem")
        .attr("class", "ylabel")
        .on("click", function (d, i: any) {
          if (handleClick) {
            if (clickReturn === "object") {
              const dt = data.find((d) => d.alt_labels === i);
              handleClick(JSON.stringify(dt), d);
            } else {
              handleClick(i, d);
            }
          }
        });

      svg
        .selectAll(".ylabel")
        .nodes()
        .forEach((element: any) => {
          const dt = data.find((d) => d.alt_labels === element.innerHTML);
          element.innerHTML = dt ? dt.label : element.innerHTML;
          const ewidth = element.getBoundingClientRect().width;
          if (ewidth > margin.left) {
            element.innerHTML =
              element.innerHTML.slice(0, margin.left / (8 * scale)) + "...";
          }
        });

      const chartcolor = "var(--barchart-blue)";

      const barClass = "barchart-bar";

      function shortNumber(value: number) {
        if (dataType == "number") {
          if (value > 999999) {
            return (value / 1000000).toFixed(2) + "M";
          } else if (value > 999) {
            return (value / 1000).toFixed(2) + "K";
          } else {
            return value;
          }
        } else if (dataType == "bandwidth") {
          return formatBandwidth(value, "B", 2);
        } else if (dataType == "percentage") {
          return value.toFixed(2) + "%";
        } else if (dataType === "time") {
          const totalSeconds = Math.floor(value);
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;
          const pad = (n: number) => n.toString().padStart(2, "0");
          return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
        }
      }

      //Hover info for bars
      const infoGroup = svg.append("g").style("pointer-events", "none");
      const infoRect = infoGroup
        .append("rect")
        .attr("fill", "var(--foreground)")
        .attr("x", 0)
        .attr("y", 0)
        .attr("rx", 4 * scale)
        .attr("height", 40 * scale);

      const infoLabel: any = infoGroup
        .append("text")
        .attr("x", 0)
        .attr("y", 0)
        .style("fill", "var(--background)")
        .style("font-size", "0.7rem");

      const infoValue: any = infoGroup
        .append("text")
        .attr("x", 0)
        .attr("y", 0)
        .style("fill", "var(--background)")
        .style("font-size", "0.7rem");

      //Bars
      const realBars = svg
        .selectAll("myRect")
        .data(data)
        .enter()
        .append("rect")
        .attr("width", 0)
        .attr("height", y.bandwidth())
        .attr("rx", 2 * scale)
        .attr("fill", (d: any) => groupColor[d.group] || chartcolor)
        .attr("x", x(0))
        .attr("y", function (d) {
          return String(y(d.alt_labels));
        });

      svg
        .selectAll("myRectHidden")
        .data(data)
        .enter()
        .append("rect")
        .attr("data-value", function (d) {
          return d.label;
        })
        .attr("class", barClass)
        .attr("fill", "transparent")
        .style("pointer-events", "none")
        .attr("x", x(0))
        .attr("y", function (d) {
          return String(y(d.label));
        })
        .attr("width", width - 20)
        .attr("height", y.bandwidth())
        .style("cursor", "pointer")
        .on("mouseover", function (_, i) {
          infoGroup.raise();
          const rects = svg.selectAll("." + barClass).nodes();
          const index = rects.indexOf(this);

          let countLabel = "Count: ";
          let countValue = i.value.toString();
          if (dataType == "bandwidth") {
            countLabel = "Bandwidth: ";
            countValue = shortNumber(i.value)?.toString() ?? "0";
          } else if (dataType == "percentage") {
            countLabel = "Percentage: ";
            countValue = i.value.toString() + "%";
          } else if (dataType == "time") {
            countLabel = "Time: ";
            countValue = shortNumber(i.value)?.toString() ?? "0";
          }

          if (customLabel !== "") {
            countLabel = customLabel + ": ";
          }

          infoLabel.text(i.label);
          infoValue.text(countLabel + countValue);

          const labelWidth = infoLabel.node().getBoundingClientRect().width;
          const infoValueWidth = infoValue.node().getBoundingClientRect().width;

          infoRect.attr(
            "width",
            Math.max(Math.max(labelWidth, infoValueWidth), 0) + 10,
          );

          infoGroup.style("opacity", 1);
          infoGroup.style("pointer-events", "all");

          d3.select(this)
            .style("transition", "all ease-in-out 0.2s")
            .style("opacity", "0.7");

          d3.select(texts.nodes()[index])
            .transition()
            .duration(500)
            .style("font-weight", "bold")
            .attr("x", x(i.value) + 5);
        })
        .on("mouseout", function () {
          const rects = svg.selectAll("." + barClass).nodes();
          const index = rects.indexOf(this);

          d3.select(this)
            .style("transition", "all ease-in-out 0.2s")
            .style("opacity", "1");

          d3.select(texts.nodes()[index])
            .transition()
            .duration(500)
            .style("font-weight", "normal")
            .attr("x", width - 20);

          infoGroup.style("opacity", 0);
          infoGroup.style("pointer-events", "none");
        })
        .on("mousemove", function (d) {
          infoRect
            .transition()
            .duration(500)
            .ease(d3.easeCubicInOut)
            .attr("x", d.offsetX - 200)
            .attr("y", d.offsetY - 60 * scale);

          infoLabel
            .transition()
            .duration(500)
            .ease(d3.easeCubicInOut)
            .attr("x", d.offsetX + 5 - 200)
            .attr("y", d.offsetY - 45 * scale);

          infoValue
            .transition()
            .duration(500)
            .ease(d3.easeCubicInOut)
            .attr("x", d.offsetX + 5 - 200)
            .attr("y", d.offsetY - 30 * scale);
        })
        .on("click", function (d, i) {
          d.preventDefault();
          if (clickReturn === "object") {
            handleClick(JSON.stringify(i), d);
          } else {
            handleClick(i.label, d);
          }
        });

      realBars
        .transition()
        .duration(1000)
        .delay(function (_d, i) {
          return i * 100;
        })
        .attr("width", function (d) {
          return Math.max(x(d.value), 0);
        });

      setTimeout(() => {
        svg.selectAll("." + barClass).style("pointer-events", "all");
      }, 2000);

      svg.selectAll("path").style("stroke", "none");
      svg.selectAll(".tick>line").style("stroke", "none");

      const texts = svg
        .selectAll("allLabels")
        .data(data)
        .enter()
        .append("text")
        .style("opacity", 0)
        .attr("x", width - 20)
        .attr("y", function (d) {
          const t = y(d.alt_labels) || 0;
          return t + y.bandwidth() / 2 + 5;
        })
        .text(function (d) {
          return shortNumber(d.value) || ""; // Ensure a valid value is returned
        })
        .style("fill", "var(--foreground)")
        .style("font-size", "0.7rem")
        .style("pointer-events", "none")
        .transition()
        .duration(500)
        .delay(function (_, i) {
          return i * 100 + 1000;
        })
        .style("opacity", 1);
    }, [data]);

    function scrollLegend(direction: "left" | "right") {
      if (!legendRef.current) return;
      const scrollAmount = 100; // Adjust this value as needed
      if (direction === "left") {
        legendRef.current.scrollBy({
          left: -scrollAmount,
          behavior: "smooth",
        });
      } else {
        legendRef.current.scrollBy({
          left: scrollAmount,
          behavior: "smooth",
        });
      }
    }

    const element = legendRef.current;
    if (element) {
      const isOverflowing = (el: any) => {
        return el.scrollWidth > el.clientWidth;
      };

      const observer = new ResizeObserver(() => {
        if (isOverflowing(element)) {
          document.querySelector(".scrollbtn")?.classList.remove("hidden");
        } else {
          document.querySelector(".scrollbtn")?.classList.add("hidden");
        }
      });

      observer.observe(element);
    }
    return (
      <div className="w-full h-full" ref={parentRef}>
        {Object.keys(groupColor).length > 0 && (
          <div className="flex gap-4 items-center">
            <div
              className="flex-1 flex gap-2 items-center overflow-x-auto no-scrollbar"
              ref={legendRef}
            >
              {Object.keys(groupColor).map((g: any) => {
                return (
                  <div className="flex gap-1 text-xs items-baseline" key={g}>
                    <div
                      className="w-2 h-2"
                      style={{
                        backgroundColor: groupColor[g],
                        borderRadius: "2px",
                      }}
                    ></div>
                    <div>{g}</div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-1 scrollbtn">
              <ChevronLeft
                size={16}
                onClick={() => scrollLegend("left")}
                className="border rounded-sm border-[gray] cursor-pointer"
              />
              <ChevronRight
                size={16}
                onClick={() => scrollLegend("right")}
                className="border rounded-sm border-[gray] cursor-pointer"
              />
            </div>
          </div>
        )}
        <svg className="overflow-visible" ref={svgRef}></svg>
      </div>
    );
  },
  arePropsEqual,
);

function arePropsEqual(prevProps: any, nextProps: any) {
  if (Array.isArray(prevProps.data) && Array.isArray(nextProps.data)) {
    if (prevProps.data.length !== nextProps.data.length) {
      return false;
    }

    for (let i = 0; i < prevProps.data.length; i++) {
      if (
        prevProps.data[i].label !== nextProps.data[i].label ||
        prevProps.data[i].value !== nextProps.data[i].value
      ) {
        return false;
      }
    }

    return true;
  }

  // Fallback to strict equality check if data is not an array
  return prevProps.data === nextProps.data;
}

BarChart.displayName = "BarChart";

export default BarChart;
